<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import AppIcon from '@/components/ui/AppIcon.vue'
import { logout, accountUrl } from '@/auth/keycloak'
import { LOCALES, LOCALE_NAMES, currentLocale, setLocale, type Locale } from '@/i18n'
import { useSessionStore } from '@/stores/session'
import { useThemeStore } from '@/stores/theme'

defineEmits<{ toggleSidebar: [] }>()

const { t } = useI18n()
const route = useRoute()
const session = useSessionStore()
const theme = useThemeStore()
const menuOpen = ref(false)

/** Route titles are keys now, so the header reads them through the bundle. */
const title = computed(() =>
  route.meta.titleKey ? t(route.meta.titleKey) : 'Loyalty Portal')

const themeIcon = computed(() =>
  theme.choice === 'light' ? 'sun' : theme.choice === 'dark' ? 'moon' : 'monitor')

const themeLabel = computed(() => {
  const name = theme.choice === 'light'
    ? t('header.themeLight')
    : theme.choice === 'dark' ? t('header.themeDark') : t('header.themeSystem')
  return `${t('header.theme')}: ${name}`
})

/**
 * The other language, as a single toggle. With exactly two there is no reason to
 * make someone open a menu to switch, and the button showing the language it
 * switches *to* is what people expect of a language toggle.
 */
const otherLocale = computed<Locale>(() =>
  LOCALES.find((locale) => locale !== currentLocale()) ?? 'en')

const otherLocaleName = computed(() => LOCALE_NAMES[otherLocale.value])
</script>

<template>
  <header class="header">
    <button class="btn btn--ghost btn--sm header__burger" type="button"
            :aria-label="t('header.menu')" @click="$emit('toggleSidebar')">
      <AppIcon name="list" />
    </button>

    <h1 class="header__title">{{ title }}</h1>

    <div class="spacer" />

    <button
      class="btn btn--ghost btn--sm header__lang"
      type="button"
      :title="t('language.switchTo', { name: otherLocaleName })"
      :aria-label="t('language.switchTo', { name: otherLocaleName })"
      lang="otherLocale"
      @click="setLocale(otherLocale)"
    >
      {{ otherLocaleName }}
    </button>

    <button class="btn btn--ghost btn--sm" type="button" :title="themeLabel"
            :aria-label="themeLabel" @click="theme.cycle()">
      <AppIcon :name="themeIcon" />
    </button>

    <div class="header__user">
      <button class="header__avatar" type="button" :aria-expanded="menuOpen"
              aria-haspopup="menu" @click="menuOpen = !menuOpen">
        <span>{{ session.initials }}</span>
      </button>

      <div v-if="menuOpen" class="menu" role="menu" @mouseleave="menuOpen = false">
        <div class="menu__head">
          <p class="menu__name">{{ session.displayName }}</p>
          <p class="menu__email">{{ session.user?.email }}</p>
          <div class="menu__roles">
            <span v-for="role in session.roles" :key="role" class="badge">{{ role }}</span>
          </div>
        </div>
        <a v-if="accountUrl()" class="menu__item" :href="accountUrl()!" target="_blank" rel="noopener">
          <AppIcon name="key" :size="14" /> {{ t('header.manageAccount') }}
        </a>
        <button class="menu__item" type="button" role="menuitem" @click="logout()">
          <AppIcon name="logout" :size="14" /> {{ t('header.signOut') }}
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: var(--header-height);
  padding: 0 1rem;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.header__title { font-size: 0.9375rem; font-weight: 600; }
.header__lang { font-weight: 600; min-width: 4.5rem; }
.header__burger { display: none; }

.header__user { position: relative; }

.header__avatar {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-inverse);
  background: var(--brand-500);
  border: none;
  border-radius: 50%;
  cursor: pointer;
}

.menu {
  position: absolute;
  top: calc(100% + 8px);
  inset-inline-end: 0;
  min-width: 250px;
  padding: 0.35rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.menu__head { padding: 0.55rem 0.6rem 0.65rem; border-bottom: 1px solid var(--border); margin-bottom: 0.35rem; }
.menu__name { margin: 0; font-size: 0.8125rem; font-weight: 600; }
.menu__email { margin: 0.1rem 0 0; font-size: 0.75rem; color: var(--text-muted); }
.menu__roles { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.45rem; }

.menu__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.6rem;
  font: inherit;
  font-size: 0.8125rem;
  color: var(--text);
  text-align: start;
  text-decoration: none;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.menu__item:hover { background: var(--surface-hover); text-decoration: none; }

@media (max-width: 900px) {
  .header__burger { display: inline-flex; }
}
</style>
